/**
 * ProjectRax — PWA UI Components
 * InstallPrompt: Add to Home Screen banner
 * OfflineIndicator: Connection status bar
 * UpdateBanner: New version available toast
 */
import { useState, useEffect } from 'react';
import { usePWA } from '../../contexts/PWAContext';
import { useI18n } from '../../contexts/I18nContext';

// ─── Install Prompt Banner ───────────────────────
export const InstallPrompt = () => {
  const { isInstallable, isInstalled, installApp, dismissInstall } = usePWA();
  const { t } = useI18n();
  const [installing, setInstalling] = useState(false);

  if (!isInstallable || isInstalled) return null;

  const handleInstall = async () => {
    setInstalling(true);
    await installApp();
    setInstalling(false);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #1B3A5C 0%, #2D5A8E 100%)',
      padding: '0.75rem 1.25rem', color: '#fff',
      boxShadow: '0 -4px 20px rgba(0,0,0,0.25)',
      animation: 'slideUp 0.4s ease-out',
    }}>
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
      <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, flexShrink: 0,
          background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src="/images/icon-96.png" alt="PRax" style={{ width: 36, height: 36, borderRadius: 8 }} onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
            {t('pwa_installTitle')}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
            {t('pwa_installDesc')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button onClick={dismissInstall} style={{
            background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
            padding: '0.45rem 1rem', borderRadius: 8, fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
          }}>
            {t('pwa_notNow')}
          </button>
          <button onClick={handleInstall} disabled={installing} style={{
            background: '#1E6FBA', border: 'none', color: '#fff',
            padding: '0.45rem 1.25rem', borderRadius: 8, fontSize: '0.82rem', cursor: 'pointer', fontWeight: 700,
            opacity: installing ? 0.7 : 1,
            boxShadow: '0 2px 10px rgba(232,121,43,0.4)',
          }}>
            {installing ? '...' : t('pwa_install')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Offline Indicator Banner ────────────────────
export const OfflineIndicator = () => {
  const { isOnline } = usePWA();
  const { t } = useI18n();
  const [showReconnect, setShowReconnect] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setShowReconnect(false);
    } else if (wasOffline) {
      setShowReconnect(true);
      const timer = setTimeout(() => {
        setShowReconnect(false);
        setWasOffline(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  // Show reconnected banner briefly
  if (showReconnect) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
        background: '#38A169', color: '#fff', padding: '0.4rem',
        textAlign: 'center', fontSize: '0.8rem', fontWeight: 600,
        animation: 'fadeIn 0.3s ease-out',
      }}>
        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        <i className="bi bi-wifi me-1"></i> {t('pwa_backOnline')}
      </div>
    );
  }

  // Show offline banner
  if (!isOnline) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
        background: '#E53E3E', color: '#fff', padding: '0.4rem',
        textAlign: 'center', fontSize: '0.8rem', fontWeight: 600,
        animation: 'fadeIn 0.3s ease-out',
      }}>
        <i className="bi bi-wifi-off me-1"></i> {t('pwa_offline')} — {t('pwa_offlineDesc')}
      </div>
    );
  }

  return null;
};

// ─── Update Available Banner ─────────────────────
export const UpdateBanner = () => {
  const { updateAvailable, applyUpdate, dismissUpdate } = usePWA();
  const { t } = useI18n();

  if (!updateAvailable) return null;

  return (
    <div style={{
      position: 'fixed', top: '1rem', right: '1rem', zIndex: 10001,
      background: '#fff', borderRadius: 12, padding: '1rem 1.25rem',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxWidth: 360,
      border: '1px solid #E2E8F0',
      animation: 'slideIn 0.4s ease-out',
    }}>
      <style>{`@keyframes slideIn { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: '#EBF8FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className="bi bi-arrow-repeat" style={{ color: '#4299E1', fontSize: '1.1rem' }}></i>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1B3A5C', marginBottom: '0.25rem' }}>
            {t('pwa_updateTitle')}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#718096', lineHeight: 1.4, marginBottom: '0.75rem' }}>
            {t('pwa_updateDesc')}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={dismissUpdate} style={{
              background: 'none', border: '1px solid #E2E8F0', color: '#718096',
              padding: '0.35rem 0.75rem', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600,
            }}>
              {t('pwa_later')}
            </button>
            <button onClick={applyUpdate} style={{
              background: '#4299E1', border: 'none', color: '#fff',
              padding: '0.35rem 0.75rem', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 700,
            }}>
              {t('pwa_updateNow')}
            </button>
          </div>
        </div>
        <button onClick={dismissUpdate} style={{
          background: 'none', border: 'none', color: '#CBD5E0', cursor: 'pointer', fontSize: '1.1rem',
          padding: 0, lineHeight: 1,
        }}>×</button>
      </div>
    </div>
  );
};

// ─── iOS Install Instructions (for Safari) ───────
export const IOSInstallHint = () => {
  const { t } = useI18n();
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Only show on iOS Safari when not in standalone mode
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const dismissed = sessionStorage.getItem('prax-ios-hint-dismissed');

    if (isIOS && isSafari && !isStandalone && !dismissed) {
      // Show after 10 seconds
      const timer = setTimeout(() => setShow(true), 10000);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!show) return null;

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem('prax-ios-hint-dismissed', 'true');
  };

  return (
    <div style={{
      position: 'fixed', bottom: '1rem', left: '1rem', right: '1rem', zIndex: 9999,
      background: '#fff', borderRadius: 14, padding: '1rem',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid #E2E8F0',
      animation: 'slideUp 0.4s ease-out',
    }}>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{ fontSize: '1.8rem', lineHeight: 1 }}>📲</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1B3A5C', marginBottom: '0.25rem' }}>
            {t('pwa_iosTitle')}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#718096', lineHeight: 1.5 }}>
            {t('pwa_iosDesc')}
          </div>
        </div>
        <button onClick={dismiss} style={{
          background: 'none', border: 'none', color: '#CBD5E0', cursor: 'pointer', fontSize: '1.2rem', padding: 0,
        }}>×</button>
      </div>
    </div>
  );
};
